import { APP_CONTEXTS } from "app/model"
import { getStore } from "app/store/store"
import { wait } from "app/tools/common"
import { CAMPAIGN_STATUSES } from "campaign/model"
import { campaignRPO } from "campaign/repository"
import { getDspDealsByCampaignId } from "dsp-deal/tools/common"
import { refreshInventory } from "form/inventory/tools/common"
import { FORM_TABS, IForm } from "form/model"
import { formActionCreator } from "form/store/actions"
import { getFormData, getNewCampaignForm, getNewGeoTargetsForm, getNewInventoryState } from "form/store/state"
import { getOrderById } from "order/tools/common"
import { NOT_FOUND_PATH } from "page/model"
import { sspFormDataLoader } from "ssp/tools/common"
import { auctionFormDataLoader } from "./auction"
import { audienceFormDataLoader } from "./audience"
import { capFormDataLoader } from "./cap"
import { creativeFormDataLoader } from "./creative"
import { mediaCreativeForecastLoader } from "./creative-forecast"
import { dspDealFormDataLoader } from "./dsp-deal"
import { environmentsFormDataLoader } from "./environments"
import { environmentForecastLoader } from "./environments-forecast"
import { coordinatesFormDataLoader } from "./geo-targets-coordinates"
import { getGeoTargetsCount } from "./geo-targets-list"
import {
  citiesFormDataLoader,
  countriesFormDataLoader,
  postalCodesFormDataLoader,
  statesFormDataLoader
} from "./geo-targets-shapes"
import { operatorFormDataLoader } from "./operator"
import { operatorForecastLoader } from "./operator-forecast"
import { scheduleFormDataLoader, weatherFormDataLoader } from "./schedule"
import { campaignSspDealFormDataLoader } from "./ssp-deal"
import { tagsFormDataLoader } from "./tags"

export function loadCampaign(
  campaignId: number,
  isForecast: boolean,
  isDspDeal: boolean,
  campaignStatus?: CAMPAIGN_STATUSES,
  tab?: FORM_TABS,
  isForm?: boolean // !== isSummary
): Promise<IForm> {
  return new Promise<IForm>(async (resolve, reject) => {
    const appContext = isForecast
      ? APP_CONTEXTS.FORECAST_EDIT
      : isDspDeal
        ? APP_CONTEXTS.DSP_DEAL_EDIT
        : APP_CONTEXTS.CAMPAIGN_EDIT
    getStore().dispatch(formActionCreator.setIsLoading(appContext, true))
    try {
      if (isNaN(campaignId)) {
        throw NOT_FOUND_PATH
      }
      while (getStore().getState().app.isInitializating) {
        await wait(50)
      }
      const campaign = isForm ? await campaignRPO.duplicate(campaignId) : await campaignRPO.getCampaign(campaignId)
      if (isDspDeal) {
        const dspDeals = await getDspDealsByCampaignId([campaign.id])
        if (dspDeals.length === 0) {
          throw Error("Missing DSP Deal")
        }
        campaign.dspDeal = dspDeals[0]
      }
      if (campaign.advertiser_id === null && typeof campaign.order_id === "number") {
        const order = await getOrderById(campaign.order_id)
        campaign.advertiser_id = order.advertiser_id
      }
      const form = getNewCampaignForm(appContext, campaign)
      const inventory = getNewInventoryState(appContext, campaign)
      const geoTargetsForm = getNewGeoTargetsForm(appContext)
      inventory.tempCampaignId = campaign.id
      form.tempCampaignId = campaign.id
      if (isDspDeal && campaign.dspDeal) {
        form.tempDspDealId = campaign.dspDeal?.id
      }
      if (isForm) {
        const originalCampaign = await campaignRPO.getCampaign(campaignId)
        if (isDspDeal) {
          const dspDeals = await getDspDealsByCampaignId([campaignId])
          if (dspDeals.length === 0) {
            throw Error("Missing original DSP Deal")
          }
          originalCampaign.dspDeal = dspDeals[0]
        }
        form.context.originalCampaign = originalCampaign
        if (!campaignStatus || campaignStatus === CAMPAIGN_STATUSES.TEMPORARY) {
          campaignStatus = originalCampaign.status_id
        }
        form.statusId = campaignStatus
        form.context.configuration.errors = {}
        form.context.configuration.isValid = true
        form.campaignId = campaignId
      }
      if (tab) {
        form.activeTab = tab
      }
      let toProcessCount = 0
      let processedCount = 0
      const onFinish = (error?: string) => {
        if (error) {
          console.error(error)
          getStore().dispatch(formActionCreator.setError(appContext, "loading"))
          return reject(`Unexpected error occured while loading campaign relations: '${error}'`)
        }
        if (++processedCount === toProcessCount) {
          inventory.totalGeoTargets = getGeoTargetsCount(form)
          const formData = getFormData(form)
          form.context.lastSavedData = formData
          form.context.initialData = formData
          getStore().dispatch(formActionCreator.summarySet(form, inventory, geoTargetsForm))
          if (!isDspDeal) {
            refreshInventory({
              campaignId: form.campaignId,
              campaignName: form.configuration.main.name,
              ignoreWarnMessage: true
            })
          }
          resolve(form)
        }
      }
      const allFormDataLoaders = [
        auctionFormDataLoader(form, error => onFinish(error)),
        capFormDataLoader(form, error => onFinish(error)),
        scheduleFormDataLoader(form, error => onFinish(error)),
        weatherFormDataLoader(form, error => onFinish(error)),
        coordinatesFormDataLoader(form, error => onFinish(error)),
        citiesFormDataLoader(form, error => onFinish(error)),
        postalCodesFormDataLoader(form, error => onFinish(error)),
        statesFormDataLoader(form, error => onFinish(error)),
        countriesFormDataLoader(form, error => onFinish(error)),
        environmentsFormDataLoader(form, error => onFinish(error)),
        sspFormDataLoader(form, error => onFinish(error)),
        audienceFormDataLoader(form, error => onFinish(error)),
        creativeFormDataLoader(form, error => onFinish(error)),
        tagsFormDataLoader(form, error => onFinish(error)),
        operatorFormDataLoader(form, error => onFinish(error)),
        campaignSspDealFormDataLoader(form, error => onFinish(error)),
        dspDealFormDataLoader(form, error => onFinish(error))
      ]
      if (isForm) {
        allFormDataLoaders.push(
          ...[
            environmentForecastLoader(form, error => onFinish(error)),
            operatorForecastLoader(form, error => onFinish(error)),
            mediaCreativeForecastLoader(form, error => onFinish(error))
          ]
        )
      }
      toProcessCount = allFormDataLoaders.length
    } catch (error) {
      console.error(error)
      getStore().dispatch(formActionCreator.setError(appContext, error === NOT_FOUND_PATH ? "not-found" : "loading"))
      reject(`An error occured while loading the form: ${error}`)
    }
  })
}
